import { Box, Stack, Typography, useTheme } from '@mui/material';
import { useMemo } from 'react';

import { useTranslate } from '@/hooks/useTranslate';

import {
  DocumentStatus,
  RequiredCondition,
  RequiredDocument,
  RequiredDocumentGroup as RequiredDocumentGroupType,
} from '@/types/document';

import { getDataTestId } from '@/utils/browser';

import { RequiredDocumentItem } from './RequiredDocumentItem';

import { RequiredDocumentsCard } from './RequiredDocuments.styled';

type RequiredDocumentGroupProps = {
  group: RequiredDocumentGroupType;
  onItemSelect: (item: RequiredDocument) => void;
  showHeader?: boolean;
  headerTitle?: string;
};

export function RequiredDocumentGroup({
  group,
  onItemSelect,
  showHeader = true,
  headerTitle,
}: Readonly<RequiredDocumentGroupProps>) {
  const { t } = useTranslate();
  const theme = useTheme();
  const isInactive = useMemo(() => {
    if (group.condition === RequiredCondition.And) {
      return false;
    }

    return group.items.some(doc =>
      [DocumentStatus.Approved, DocumentStatus.InReview].includes(
        doc.status as DocumentStatus,
      ),
    );
  }, [group.condition, group.items]);

  if (!group.items.length) return null;

  const hasCountryTitle = !!group.title;
  const resolvedHeaderTitle =
    headerTitle ??
    (group.condition === RequiredCondition.And
      ? t('moreInfo.requiredDocumentGroup.uploadAll')
      : t('moreInfo.requiredDocumentGroup.uploadOne'));

  return (
    <Stack
      className="RequiredDocumentGroup"
      gap={2}
      direction="column"
      width="100%"
      {...getDataTestId(`required-document-group-${group.condition}`)}
    >
      {showHeader && hasCountryTitle && (
        <Box width="100%" textAlign="center">
          <Typography
            variant="h5"
            component="div"
            display="block"
            textAlign="center"
          >
            {group.title}
          </Typography>
          {group.subtitle && (
            <Typography
              variant="caption"
              component="div"
              display="block"
              textAlign="center"
              color={theme.palette.text.secondary}
              mt={0.5}
            >
              {t(group.subtitle)}
            </Typography>
          )}
        </Box>
      )}
      {showHeader && !hasCountryTitle && (
        <Typography variant="h3" textAlign="center">
          {resolvedHeaderTitle}
        </Typography>
      )}
      <RequiredDocumentsCard>
        {group.items.map(item => (
          <RequiredDocumentItem
            {...getDataTestId(`required-document-item-${item.name}`)}
            key={`required-document-item-${item.name}`}
            doc={item}
            isInactive={isInactive}
            onClick={() => onItemSelect(item)}
          />
        ))}
      </RequiredDocumentsCard>
    </Stack>
  );
}
